import React, { useState, useEffect } from 'react'
import { InputText } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import {
  MAX_LENGTH,
  LATIN_LETTERS_WITH_NUMBERS
} from '../../../constants/constants'
import { usePresetValue, usePrevious } from '../../utils/hooks'
import { getUserValue } from '../utils'

const ExternalSwiftCode = ({ i18nLabel, type, name, maxlength, additionalInfo, required, allowNonLatin }) => {
  const { t } = useTranslation()
  const label = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [isFilled, setIsFilled] = useState(false)
  const [error, setError] = useState('')
  const [isShown, setIsShown] = useState(true)
  const [{
    whitelabel: { extFields },
    form,
    form: { accId },
    user
  }, dispatch] = useStore()
  const { isSwiftOrIban: swiftOrIbanFldValue } = form[accId]
  const prevSwiftOrIbanFldValue = usePrevious(swiftOrIbanFldValue)
  const userValue = getUserValue(accId, user, name)

  useEffect(() => {
    if (swiftOrIbanFldValue && prevSwiftOrIbanFldValue === swiftOrIbanFldValue) return
    const swiftOrIbanFld = extFields.find(fld => fld.name === 'isSwiftOrIban')
    if (!swiftOrIbanFld) return

    if (swiftOrIbanFld && !swiftOrIbanFldValue) {
      setIsShown(false)
      dispatch({ type: 'removeValidationFld', name })
      return
    }

    const isShown = swiftOrIbanFldValue === name
    setIsShown(isShown)
    if (isShown) {
      dispatch({ type: 'addValidationFld', name })
      if (value.length) {
        dispatch({ type: 'setExtFieldValue', name, value })
        dispatch({ type: 'setValidationFld', name, value: isValid })
      }
    } else {
      dispatch({ type: 'removeValidationFld', name })
      dispatch({ type: 'removeFieldValue', name })
    }
  }, [swiftOrIbanFldValue, value, isValid])

  const presetValue = (value) => {
    setValue(value)
    const isValid = getIsValid(value)
    setIsValid(isValid)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
    setError(getErrorMsg(name, value))
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  usePresetValue(name, userValue, presetValue)

  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
  }

  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    const isValid = getIsValid(trimmedValue)

    if (isValid) {
      setIsValid(true)
      setError('')
      dispatch({ type: 'setExtFieldValue', name, value })
    } else {
      setIsValid(false)
      setError(getErrorMsg(name, value))
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  const getIsValid = (value) => allowNonLatin ? !!value.length : LATIN_LETTERS_WITH_NUMBERS.test(value)

  /**
   * @param {string} name
   * @param {string} value
   * @returns {string}
   */
  const getErrorMsg = (name, value) => {
    return !isFilled || getIsValid(value) ? '' : 'generatedField.onlyLatin'
  }

  return isShown ? (
    <InputText
      name={name}
      label={label}
      value={value}
      type={type}
      key={name}
      maxLength={maxlength || MAX_LENGTH}
      error={t(error)}
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
    />
  ) : null
}

const getExternalSwiftCode = textField => {
  const { i18n, name, required, type, order, maxLength, additionalInfo, allowNonLatin } = textField
  return (
    <ExternalSwiftCode
      i18nLabel={i18n}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
      additionalInfo={additionalInfo}
      maxlength={maxLength}
      allowNonLatin={allowNonLatin}
    />
  )
}

export { ExternalSwiftCode, getExternalSwiftCode }
